import { z } from 'zod'
import * as Sentry from '@sentry/browser'
import { parseTimestamp } from '@/util/zod/validators'

export enum ActivityEventType {
  ChatUpdated = 'chat_updated',
  DraftGenerated = 'draft_generated',
  DraftUpdated = 'draft_updated',
  TableCreated = 'table_created',
  TableUpdated = 'table_updated',
  RecordGenerated = 'record_generated',
  RecordUpdated = 'record_updated',
}

export enum MatterRecordType {
  MedChron = 'med_chron',
  MedSummary = 'med_summary',
  Timeline = 'timeline',
}

const ActivityFeedRowBaseSchema = z.object({
  occurred_at: z.preprocess((value, context) => parseTimestamp(value, context), z.number()),
  actor_user_id: z.string().min(1),
})

const ChatUpdatedRowSchema = ActivityFeedRowBaseSchema.extend({
  event: z.literal(ActivityEventType.ChatUpdated),
  conversation_id: z.string().uuid(),
  conversation_title: z.string().nullable(),
})

/* Both draft events carry the same fields; only the copy differs. */
const DraftRowSchema = ActivityFeedRowBaseSchema.extend({
  /* Stable across edits, unlike the version id. The re-entry analytics event wants this one. */
  artifact_id: z.string().uuid(),
  artifact_version_id: z.string().uuid(),
  draft_title: z.string().nullable(),
  /* The chat the draft came from. */
  conversation_id: z.string().uuid(),
  conversation_title: z.string().nullable(),
})

const TableRowSchema = ActivityFeedRowBaseSchema.extend({
  table_id: z.string().uuid(),
  table_name: z.string(),
})

const RecordGeneratedRowSchema = ActivityFeedRowBaseSchema.extend({
  event: z.literal(ActivityEventType.RecordGenerated),
  record_type: z.nativeEnum(MatterRecordType),
  /* Files in the initial build, not the record's current file count. */
  file_count: z.number(),
})

const RecordUpdatedRowSchema = ActivityFeedRowBaseSchema.extend({
  event: z.literal(ActivityEventType.RecordUpdated),
  record_type: z.nativeEnum(MatterRecordType),
})

export const ActivityFeedRowSchema = z.discriminatedUnion('event', [
  ChatUpdatedRowSchema,
  DraftRowSchema.extend({ event: z.literal(ActivityEventType.DraftGenerated) }),
  DraftRowSchema.extend({ event: z.literal(ActivityEventType.DraftUpdated) }),
  TableRowSchema.extend({ event: z.literal(ActivityEventType.TableCreated) }),
  TableRowSchema.extend({ event: z.literal(ActivityEventType.TableUpdated) }),
  RecordGeneratedRowSchema,
  RecordUpdatedRowSchema,
])

export type ActivityFeedRow = z.infer<typeof ActivityFeedRowSchema>

type DraftActivityRow = Extract<ActivityFeedRow, { artifact_id: string }>

/** A draft row, whichever of the two draft events it carries. */
export function isDraftRow(row: ActivityFeedRow): row is DraftActivityRow {
  return row.event === ActivityEventType.DraftGenerated || row.event === ActivityEventType.DraftUpdated
}

/**
 * Keeps the feed renderable when one row isn't: a row carrying an event type added after this build
 * shipped is dropped and reported, rather than failing the page it arrived on.
 */
const ActivityFeedRowsSchema = z.array(z.unknown()).transform((rows) => {
  const validRows: ActivityFeedRow[] = []
  const droppedEvents: unknown[] = []
  let firstError: z.ZodError | undefined

  for (const row of rows) {
    const parsed = ActivityFeedRowSchema.safeParse(row)
    if (parsed.success) {
      validRows.push(parsed.data)
      continue
    }
    droppedEvents.push(typeof row === 'object' && row !== null ? (row as { event?: unknown }).event : undefined)
    firstError ??= parsed.error
  }

  // Reported once for the whole response: an event type the endpoint adds later would otherwise
  // raise one message per row, on every fetch, for everyone on a matter overview.
  if (droppedEvents.length > 0) {
    console.error('Dropped invalid activity feed rows', firstError)
    Sentry.captureMessage('Activity feed: dropped invalid rows', {
      extra: { droppedCount: droppedEvents.length, events: [...new Set(droppedEvents)], issues: firstError?.issues.slice(0, 5) },
    })
  }

  return validRows
})

export const ActivityFeedResponseSchema = z.object({
  rows: ActivityFeedRowsSchema,
  /* True when the matter has activity beyond the rows returned, which the feed never fetches. */
  has_more: z.boolean(),
})

export type ActivityFeedResponse = z.infer<typeof ActivityFeedResponseSchema>
